import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { updateInvoice, type SalesInvoice } from '@/api/salesInvoice';
import { formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';

interface Props {
  inv: SalesInvoice;
  onDone: () => void;
  onClose: () => void;
}

const HEADER = [
  { key: 'paymentTerms', label: 'Payment terms' },
  { key: 'dueDate', label: 'Due date', type: 'date' },
  { key: 'vehicleNo', label: 'Vehicle no.' },
  { key: 'lrNo', label: 'LR no.' },
  { key: 'ewayBillNo', label: 'E-way bill no.' },
  { key: 'irn', label: 'IRN (e-invoice)' },
] as const;
type HeaderKey = (typeof HEADER)[number]['key'] | 'bankDetails' | 'partialNote';

/** Edit a Draft invoice: details plus each line's rate and discount. Qty and items stay as dispatched. */
export function InvoiceEditPanel({ inv, onDone, onClose }: Props) {
  const toast = useToast();
  const startHeader = (): Record<HeaderKey, string> => ({
    paymentTerms: inv.paymentTerms ?? '',
    dueDate: inv.dueDate ? inv.dueDate.slice(0, 10) : '',
    vehicleNo: inv.vehicleNo ?? '',
    lrNo: inv.lrNo ?? '',
    ewayBillNo: inv.ewayBillNo ?? '',
    irn: inv.irn ?? '',
    bankDetails: inv.bankDetails ?? '',
    partialNote: inv.partialNote ?? '',
  });
  const [head, setHead] = useState(startHeader);
  const [lines, setLines] = useState(() =>
    Object.fromEntries(inv.items.map((it) => [it.id, { rate: String(Number(it.rate)), disc: String(Number(it.discountAmt)) }])),
  );
  const [reason, setReason] = useState('');

  const orig = startHeader();
  const changedHead = (Object.keys(head) as HeaderKey[]).filter((k) => head[k].trim() !== orig[k]);
  const changedLines = inv.items.filter(
    (it) => Number(lines[it.id].rate) !== Number(it.rate) || Number(lines[it.id].disc) !== Number(it.discountAmt),
  );
  const net = (id: number, qty: number) => Number(lines[id].rate || 0) * qty - Number(lines[id].disc || 0);
  const newTotal = inv.items.reduce((s, it) => s + net(it.id, Number(it.qty)), 0) + Number(inv.freightCharges);
  const badLine = inv.items.find(
    (it) => !(Number(lines[it.id].rate) >= 0) || !(Number(lines[it.id].disc) >= 0) || net(it.id, Number(it.qty)) < 0,
  );
  const nothing = !changedHead.length && !changedLines.length;

  const save = useMutation({
    mutationFn: () =>
      updateInvoice(inv.id, {
        ...Object.fromEntries(changedHead.map((k) => [k, head[k].trim()])),
        ...(changedLines.length
          ? { lines: changedLines.map((it) => ({ id: it.id, rate: Number(lines[it.id].rate), discountAmt: Number(lines[it.id].disc) })) }
          : {}),
        reason: reason.trim(),
      }),
    onSuccess: (r) => {
      toast.push(`Invoice ${r.invoiceNo} updated`, 'success');
      onDone();
    },
    onError: (e) => toast.push(apiError(e, 'Could not save the invoice'), 'error'),
  });

  const setLine = (id: number, k: 'rate' | 'disc', v: string) => setLines((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  return (
    <div className="space-y-2 rounded-md border border-accent/40 bg-[var(--bg-soft)] p-2 sm:p-3">
      <div className="text-[10px] font-bold uppercase text-[var(--text-muted)]">Edit Invoice (draft)</div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {HEADER.map((f) => (
          <FormGroup key={f.key} label={f.label}>
            <Input
              type={'type' in f ? f.type : 'text'}
              value={head[f.key]}
              onChange={(e) => setHead((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          </FormGroup>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <FormGroup label="Bank details">
          <Textarea rows={2} value={head.bankDetails} onChange={(e) => setHead((p) => ({ ...p, bankDetails: e.target.value }))} />
        </FormGroup>
        <FormGroup label="Note">
          <Textarea rows={2} value={head.partialNote} onChange={(e) => setHead((p) => ({ ...p, partialNote: e.target.value }))} />
        </FormGroup>
      </div>

      <div className="text-[10px] font-bold uppercase text-[var(--text-muted)]">Rates &amp; discounts</div>
      <div className="space-y-1.5">
        {inv.items.map((it) => {
          const qty = Number(it.qty);
          const changed = changedLines.includes(it);
          return (
            <div
              key={it.id}
              className={`grid grid-cols-[1fr_5.5rem_5.5rem] items-center gap-2 rounded-md border p-1.5 sm:grid-cols-[1fr_7rem_7rem_7rem] ${changed ? 'border-accent/60' : 'border-[var(--border-soft)]'}`}
            >
              <div className="col-span-3 min-w-0 sm:col-span-1">
                <div className="truncate text-[12px] font-bold text-[var(--text-primary)]">{it.itemName}</div>
                <div className="text-[10px] text-[var(--text-muted)]">
                  {qty} {it.unit} · GST {Number(it.gstPct)}%
                </div>
              </div>
              <label className="text-[10px] text-[var(--text-muted)]">
                Rate (MRP)
                <Input type="number" min={0} step="0.01" value={lines[it.id].rate} onChange={(e) => setLine(it.id, 'rate', e.target.value)} />
              </label>
              <label className="text-[10px] text-[var(--text-muted)]">
                Line discount ₹
                <Input type="number" min={0} step="0.01" value={lines[it.id].disc} onChange={(e) => setLine(it.id, 'disc', e.target.value)} />
              </label>
              <div className="text-right text-[12px] font-extrabold text-accent">{formatINR(net(it.id, qty))}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-baseline justify-end gap-3 text-[11px]">
        <span className="text-[var(--text-muted)]">Grand total</span>
        {changedLines.length > 0 && <span className="text-[var(--text-muted)] line-through">{formatINR(Number(inv.grandTotal))}</span>}
        <span className="text-sm font-extrabold text-accent">{formatINR(newTotal)}</span>
      </div>
      {changedLines.length > 0 && (
        <div className="text-right text-[10px] text-[var(--text-muted)]">GST is re-worked from the new amounts when you save.</div>
      )}

      <FormGroup label="Reason for change (kept in Invoice Logs)">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormGroup>
      {badLine && <div className="text-[11px] text-danger">Check {badLine.itemName}: the discount can't be more than the line amount.</div>}
      <div className="flex justify-end gap-2">
        <Button variant="glass" size="sm" onClick={onClose}>
          Back
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => save.mutate()}
          disabled={nothing || !reason.trim() || !!badLine || save.isPending}
          title={nothing ? 'Nothing changed yet' : !reason.trim() ? 'Give a reason' : undefined}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
